import React, { useState, useEffect } from 'react'
import { getLoggingService, getLogSettings, type MessageActivityLog, type LogFilter } from 'edge-stream-js'
import { ActivityLogViewer, ErrorBoundary } from './components'
import { JSONVisualizer } from '@edge-stream/app-ui-components-react'
import { DiffEngine } from 'edge-stream-js'
import {
  BarChartIcon, TrendingUpIcon, ClockIcon, SearchIcon, SettingsIcon,
  WrenchIcon, DownloadIcon, RefreshIcon, TrashIcon,
  CheckCircleIcon, XCircleIcon, HourglassIcon, SkipForwardIcon,
  ArrowUpIcon, ArrowDownIcon, SignalIcon, HooksIcon, MailboxIcon,
  NoteIcon, ChevronDownIcon, ChevronRightIcon, CheckIcon, CloseIcon,
  PackageIcon, UserIcon, ClipboardIcon, PencilIcon, ZapIcon,
} from '@bizfirst/common-react'
import {
  MessageEnvelopeIcon, OutboxIcon, InboxIcon, TagIcon,
} from '@edge-stream/app-ui-components-react'

// Revoking right after click() can race the browser's save and corrupt the download;
// keep the blob URL alive long enough for the download to start.
const BLOB_URL_REVOKE_DELAY_MS = 2000

type ViewMode = 'overview' | 'timeline' | 'details' | 'settings'
type FilterStatus = 'all' | 'completed' | 'failed' | 'processing'

const STORAGE_KEY = 'activity-stream-view-mode'

function getStoredViewMode(): ViewMode {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored && ['overview', 'timeline', 'details', 'settings'].includes(stored)) {
      return stored as ViewMode
    }
  } catch {}
  return 'overview'
}

function saveViewMode(mode: ViewMode): void {
  try {
    localStorage.setItem(STORAGE_KEY, mode)
  } catch {}
}

export function ActivityStreamPage(): React.ReactElement {
  const loggingService = getLoggingService()
  const logSettings = getLogSettings()

  // View state
  const [viewMode, setViewMode] = useState<ViewMode>(getStoredViewMode())
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null)

  // Filter state
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all')
  const [filterServerId, setFilterServerId] = useState<string>('')
  const [filterTopic, setFilterTopic] = useState<string>('')
  const [searchText, setSearchText] = useState<string>('')

  // Logging settings state
  const [hookLoggingEnabled, setHookLoggingEnabled] = useState(logSettings.isHookLoggingEnabled())
  const [subscriberLoggingEnabled, setSubscriberLoggingEnabled] = useState(
    logSettings.isSubscriberLoggingEnabled(),
  )
  const [logMessageBody, setLogMessageBody] = useState(logSettings.shouldLogHookMessageBody())
  const [logMetadata, setLogMetadata] = useState(logSettings.shouldLogHookMetadata())
  const [persistenceEnabled, setPersistenceEnabled] = useState(logSettings.isPersistenceEnabled())
  const [maxLogEntries, setMaxLogEntries] = useState(logSettings.getMaxLogEntries())

  // Data state
  const [logs, setLogs] = useState<MessageActivityLog[]>([])
  const [selectedLog, setSelectedLog] = useState<MessageActivityLog | null>(null)
  const [statistics, setStatistics] = useState(loggingService.getStatistics())

  // Refresh logs periodically
  useEffect(() => {
    const interval = setInterval(() => {
      refreshLogs()
    }, 1000)

    return () => clearInterval(interval)
  }, [filterStatus, filterServerId, filterTopic, searchText])

  const refreshLogs = () => {
    const filter: LogFilter = {
      status: filterStatus === 'all' ? undefined : (filterStatus as any),
      serverId: filterServerId || undefined,
      topic: filterTopic || undefined,
    }

    const filtered = loggingService.queryLogs(filter)
    const searched = searchText
      ? filtered.filter(
          (log) =>
            log.messageId.includes(searchText) ||
            log.messageType.includes(searchText) ||
            log.topic.includes(searchText),
        )
      : filtered

    setLogs(searched.sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime()))
    setStatistics(loggingService.getStatistics())
  }

  const handleChangeViewMode = (mode: ViewMode) => {
    setViewMode(mode)
    saveViewMode(mode)
  }

  const handleSelectMessage = (messageId: string) => {
    setSelectedMessageId(messageId)
    const log = loggingService.getMessageLog(messageId)
    setSelectedLog(log || null)
    handleChangeViewMode('details')
  }

  const handleHookLoggingToggle = (enabled: boolean) => {
    setHookLoggingEnabled(enabled)
    logSettings.setHookLoggingEnabled(enabled)
  }

  const handleSubscriberLoggingToggle = (enabled: boolean) => {
    setSubscriberLoggingEnabled(enabled)
    logSettings.setSubscriberLoggingEnabled(enabled)
  }

  const handleLogMessageBodyToggle = (enabled: boolean) => {
    setLogMessageBody(enabled)
    logSettings.setHookLogMessageBody(enabled)
  }

  const handleLogMetadataToggle = (enabled: boolean) => {
    setLogMetadata(enabled)
    logSettings.setHookLogMetadata(enabled)
  }

  const handlePersistenceToggle = (enabled: boolean) => {
    setPersistenceEnabled(enabled)
    logSettings.setPersistenceEnabled(enabled)
  }

  const handleMaxLogEntriesChange = (value: number) => {
    setMaxLogEntries(value)
    logSettings.setMaxLogEntries(value)
  }

  const handleClearLogs = () => {
    if (confirm('Are you sure you want to clear all logs?')) {
      loggingService.clearAllLogs()
      setLogs([])
      setSelectedLog(null)
      refreshLogs()
    }
  }

  const handleExportLogs = () => {
    const json = loggingService.exportLogsAsJSON()
    const blob = new Blob([json], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `logs-${new Date().toISOString()}.json`
    a.click()
    setTimeout(() => URL.revokeObjectURL(url), BLOB_URL_REVOKE_DELAY_MS)
  }

  return (
    <ErrorBoundary>
      <div className="page-container">
        <h2 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><BarChartIcon /> Activity Stream</h2>

      {/* Tab Navigation */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', borderBottom: '1px solid var(--border)' }}>
        {(['overview', 'timeline', 'details', 'settings'] as ViewMode[]).map((mode) => (
          <button
            key={mode}
            onClick={() => handleChangeViewMode(mode)}
            style={{
              padding: '8px 16px',
              background: viewMode === mode ? 'var(--obs-primary, #4ad059)' : 'transparent',
              color: viewMode === mode ? 'white' : 'var(--text-primary)',
              border: 'none',
              borderBottom: viewMode === mode ? '2px solid var(--color-brand, #427A43)' : 'none',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: viewMode === mode ? 600 : 400,
            }}
          >
            {mode === 'overview' && <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><TrendingUpIcon /> Overview</span>}
            {mode === 'timeline' && <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><ClockIcon /> Timeline</span>}
            {mode === 'details' && <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><SearchIcon /> Details</span>}
            {mode === 'settings' && <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><SettingsIcon /> Settings</span>}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {viewMode === 'overview' && (
        <div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '16px',
              marginBottom: '24px',
            }}
          >
            <StatCard label="Total Messages" value={statistics.totalMessages.toString()} />
            <StatCard label="Successful" value={statistics.successMessages.toString()} />
            <StatCard label="Failed" value={statistics.failedMessages.toString()} />
            <StatCard label="Avg Processing" value={`${statistics.averageProcessingTime.toFixed(0)}ms`} />
            <StatCard label="Total Errors" value={statistics.errorsCount.toString()} />
            <StatCard label="Topics" value={statistics.topicsProcessed.length.toString()} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
            <div
              style={{
                padding: '16px',
                background: 'rgba(15, 23, 42, 0.95)',
                borderRadius: '12px',
                border: '1px solid rgba(58, 140, 69, 0.4)',
              }}
            >
              <h4 style={{ margin: '0 0 12px 0', color: 'var(--primary)', display: 'flex', alignItems: 'center', gap: '6px' }}><ClockIcon /> Slowest Message</h4>
              <p style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>
                {statistics.slowestMessage.time.toFixed(0)}ms
              </p>
              <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: 'var(--text-secondary)' }}>
                ID: {statistics.slowestMessage.messageId.substring(0, 8)}...
              </p>
            </div>

            <div
              style={{
                padding: '16px',
                background: 'rgba(15, 23, 42, 0.95)',
                borderRadius: '12px',
                border: '1px solid rgba(58, 140, 69, 0.4)',
              }}
            >
              <h4 style={{ margin: '0 0 12px 0', color: 'var(--primary)', display: 'flex', alignItems: 'center', gap: '6px' }}><WrenchIcon /> Slowest Hook</h4>
              <p style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>
                {statistics.slowestHook.time.toFixed(0)}ms
              </p>
              <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: 'var(--text-secondary)' }}>
                Hook: {statistics.slowestHook.hookName}
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              className="button button-secondary"
              onClick={handleExportLogs}
              style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <DownloadIcon /> Export Logs
            </button>
            <button
              className="button button-secondary"
              onClick={refreshLogs}
              style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <RefreshIcon /> Refresh
            </button>
            <button
              className="button button-danger"
              onClick={handleClearLogs}
              style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <TrashIcon /> Clear All
            </button>
          </div>
        </div>
      )}

      {/* Timeline Tab */}
      {viewMode === 'timeline' && (
        <div>
          {/* Filters */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '12px',
              marginBottom: '20px',
              padding: '16px',
              background: 'rgba(15, 23, 42, 0.95)',
              border: '1px solid rgba(58, 140, 69, 0.4)',
              borderRadius: '12px',
            }}
          >
            <input
              type="text"
              placeholder="Search by message ID, type, or topic..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="input"
            />

            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value as FilterStatus)} className="input">
              <option value="all">All Status</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
              <option value="processing">Processing</option>
            </select>

            <input
              type="text"
              placeholder="Filter by Server ID..."
              value={filterServerId}
              onChange={(e) => setFilterServerId(e.target.value)}
              className="input"
            />

            <input
              type="text"
              placeholder="Filter by Topic..."
              value={filterTopic}
              onChange={(e) => setFilterTopic(e.target.value)}
              className="input"
            />
          </div>

          {/* Logs List */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              maxHeight: '600px',
              overflowY: 'auto',
            }}
          >
            {logs.length === 0 ? (
              <div
                style={{
                  padding: '32px',
                  textAlign: 'center',
                  color: 'var(--text-secondary)',
                }}
              >
                No logs found
              </div>
            ) : (
              logs.map((log) => (
                <div
                  key={log.messageId}
                  onClick={() => handleSelectMessage(log.messageId)}
                  style={{
                    padding: '12px',
                    background: selectedMessageId === log.messageId ? 'rgba(52, 211, 153, 0.15)' : 'var(--bg-secondary)',
                    border: selectedMessageId === log.messageId ? '1px solid #34d399' : '1px solid var(--border)',
                    borderLeft: `4px solid ${log.status === 'completed' ? '#10b981' : log.status === 'failed' ? '#ef4444' : '#f59e0b'}`,
                    borderRadius: '6px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '10px', color: '#8b5cf6', fontWeight: 700, textTransform: 'uppercase' }}>ID</span>
                      <span style={{ fontWeight: 600, fontSize: '12px', color: log.status === 'completed' ? '#10b981' : log.status === 'failed' ? '#ef4444' : '#f59e0b' }}>
                        {log.messageId.substring(0, 12)}...
                      </span>
                    </div>
                    <span
                      style={{
                        fontSize: '11px',
                        padding: '4px 10px',
                        background: log.status === 'completed' ? '#10b981' : log.status === 'failed' ? '#ef4444' : '#f59e0b',
                        color: 'white',
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        fontWeight: 600,
                      }}
                    >
                      {log.status === 'completed' ? <CheckCircleIcon /> : log.status === 'failed' ? <XCircleIcon /> : <HourglassIcon />} {log.status}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: '16px', fontSize: '11px', color: 'var(--text-secondary)', flexWrap: 'wrap' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#3b82f6', fontWeight: 500 }}>
                      <span style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase' }}>Type</span>
                      <ArrowUpIcon /> {log.messageType}
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#ec4899', fontWeight: 500 }}>
                      <span style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase' }}>Topic</span>
                      <SignalIcon /> {log.topic}
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#f59e0b', fontWeight: 500 }}>
                      <span style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase' }}>Time</span>
                      <ClockIcon /> {log.totalTime}ms
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#8b5cf6', fontWeight: 500 }}>
                      <HooksIcon /> {log.summary.totalHooks} hooks
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#06b6d4', fontWeight: 500 }}>
                      <MailboxIcon /> {log.summary.totalSubscribers} subs
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Details Tab — empty state */}
      {viewMode === 'details' && !selectedLog && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px', color: 'var(--text-secondary)', padding: '60px 0' }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ width: '48px', height: '48px', opacity: 0.4 }}>
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <p style={{ margin: 0, fontSize: '15px', fontWeight: 600 }}>No event selected</p>
          <p style={{ margin: 0, fontSize: '13px' }}>
            Go to the{' '}
            <button className="button button-secondary" style={{ padding: '4px 10px', fontSize: '13px' }} onClick={() => handleChangeViewMode('timeline')}>
              Timeline
            </button>{' '}
            tab and click an event to inspect its details.
          </p>
        </div>
      )}

      {/* Details Tab */}
      {viewMode === 'details' && selectedLog && (
        <div>
          <div style={{ marginBottom: '20px' }}>
            <button className="button button-secondary" onClick={() => handleChangeViewMode('timeline')}>
              ← Back to Timeline
            </button>
          </div>
          <MessageDetailView log={selectedLog} />
        </div>
      )}

      {/* Settings Tab */}
      {viewMode === 'settings' && (
        <div style={{ maxWidth: '600px' }}>
          <div
            style={{
              padding: '20px',
              background: 'rgba(15, 23, 42, 0.95)',
              borderRadius: '12px',
              border: '1px solid rgba(58, 140, 69, 0.4)',
              display: 'flex',
              flexDirection: 'column',
              gap: '0',
            }}
          >
            <h3 style={{ margin: '0 0 20px 0', color: 'var(--primary)' }}>Logging Configuration</h3>

            {/* Hook Logging */}
            <div style={{ marginBottom: '28px', paddingBottom: '28px', borderBottom: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                <input
                  type="checkbox"
                  id="hookLogging"
                  checked={hookLoggingEnabled}
                  onChange={(e) => handleHookLoggingToggle(e.target.checked)}
                  style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                />
                <label htmlFor="hookLogging" style={{ cursor: 'pointer', fontWeight: 600, margin: 0 }}>
                  <HooksIcon /> Enable Hook Logging
                </label>
              </div>

              {hookLoggingEnabled && (
                <div style={{ marginLeft: '30px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <LabeledCheckbox
                    id="logBody"
                    label="Log Message Body"
                    checked={logMessageBody}
                    onChange={handleLogMessageBodyToggle}
                    description="Includes full message content in logs"
                  />
                  <LabeledCheckbox
                    id="logMetadata"
                    label="Log Metadata"
                    checked={logMetadata}
                    onChange={handleLogMetadataToggle}
                    description="Includes message metadata and attributes"
                  />
                </div>
              )}
            </div>

            {/* Subscriber Logging */}
            <div style={{ marginBottom: '28px', paddingBottom: '28px', borderBottom: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                <input
                  type="checkbox"
                  id="subscriberLogging"
                  checked={subscriberLoggingEnabled}
                  onChange={(e) => handleSubscriberLoggingToggle(e.target.checked)}
                  style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                />
                <label htmlFor="subscriberLogging" style={{ cursor: 'pointer', fontWeight: 600, margin: 0 }}>
                  <MailboxIcon /> Enable Subscriber Logging
                </label>
              </div>
            </div>

            {/* Persistence */}
            <div style={{ marginBottom: '0px', paddingBottom: '0px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                <input
                  type="checkbox"
                  id="persistence"
                  checked={persistenceEnabled}
                  onChange={(e) => handlePersistenceToggle(e.target.checked)}
                  style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                />
                <label htmlFor="persistence" style={{ cursor: 'pointer', fontWeight: 600, margin: 0 }}>
                  <DownloadIcon /> Enable Log Persistence
                </label>
              </div>

              {persistenceEnabled && (
                <div style={{ marginLeft: '30px' }}>
                  <label style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                    Max Log Entries: {maxLogEntries}
                  </label>
                  <input
                    type="range"
                    min="100"
                    max="10000"
                    step="100"
                    value={maxLogEntries}
                    onChange={(e) => handleMaxLogEntriesChange(parseInt(e.target.value))}
                    style={{ width: '100%', marginTop: '8px', cursor: 'pointer' }}
                  />
                </div>
              )}
            </div>

            {/* Status */}
            <div
              style={{
                padding: '16px',
                background: 'rgba(58, 140, 69, 0.1)',
                borderRadius: '12px',
                fontSize: '12px',
                color: 'var(--text-secondary)',
                border: '1px solid rgba(58, 140, 69, 0.3)',
                marginTop: '8px',
              }}
            >
              <strong style={{ color: 'var(--text-primary)' }}>Current Status:</strong> {loggingService.getTotalLogCount()} logs in memory
            </div>
          </div>
        </div>
      )}
      </div>
    </ErrorBoundary>
  )
}

// ─── Helper Components ───────────────────────────────────────────

function StatCard({ label, value, icon }: { label: string; value: string; icon?: string }) {
  return (
    <div
      style={{
        padding: '16px',
        background: 'rgba(15, 23, 42, 0.95)',
        borderRadius: '12px',
        border: '1px solid rgba(58, 140, 69, 0.4)',
        textAlign: 'center',
      }}
    >
      {icon && <div style={{ fontSize: '24px', marginBottom: '8px' }}>{icon}</div>}
      <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '8px' }}>{label}</div>
      <div style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--primary)' }}>{value}</div>
    </div>
  )
}

function LabeledCheckbox({
  id,
  label,
  checked,
  onChange,
  description,
}: {
  id: string
  label: string
  checked: boolean
  onChange: (checked: boolean) => void
  description?: string
}) {
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
        <input
          type="checkbox"
          id={id}
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          style={{ cursor: 'pointer', width: '16px', height: '16px' }}
        />
        <label htmlFor={id} style={{ cursor: 'pointer', margin: 0, fontWeight: 500, fontSize: '13px' }}>
          {label}
        </label>
      </div>
      {description && (
        <div style={{ marginLeft: '24px', fontSize: '11px', color: 'var(--text-secondary)' }}>{description}</div>
      )}
    </div>
  )
}

function MessageDetailView({ log }: { log: MessageActivityLog }) {
  const [expandedHooks, setExpandedHooks] = useState<Set<string>>(new Set())
  const [expandedSubscribers, setExpandedSubscribers] = useState<Set<string>>(new Set())
  const [expandedRawMessage, setExpandedRawMessage] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem('activity-stream-raw-message-expanded')
      return stored ? JSON.parse(stored) : true
    } catch {
      return true
    }
  })

  const toggleHookExpanded = (hookName: string) => {
    const newSet = new Set(expandedHooks)
    if (newSet.has(hookName)) {
      newSet.delete(hookName)
    } else {
      newSet.add(hookName)
    }
    setExpandedHooks(newSet)
  }

  const toggleSubscriberExpanded = (subscriberId: string) => {
    const newSet = new Set(expandedSubscribers)
    if (newSet.has(subscriberId)) {
      newSet.delete(subscriberId)
    } else {
      newSet.add(subscriberId)
    }
    setExpandedSubscribers(newSet)
  }

  const toggleRawMessageExpanded = () => {
    const newValue = !expandedRawMessage
    setExpandedRawMessage(newValue)
    try {
      localStorage.setItem('activity-stream-raw-message-expanded', JSON.stringify(newValue))
    } catch {}
  }


  return (
    <div>
      {/* Message Metadata */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '16px',
          marginBottom: '24px',
        }}
      >
        <div
          style={{
            padding: '12px',
            background: 'var(--bg-secondary)',
            borderRadius: '6px',
            border: '1px solid var(--border)',
          }}
        >
          <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Message ID</div>
          <div style={{ fontSize: '12px', fontFamily: 'monospace', wordBreak: 'break-all' }}>{log.messageId}</div>
        </div>

        <div
          style={{
            padding: '12px',
            background: 'var(--bg-secondary)',
            borderRadius: '6px',
            border: '1px solid var(--border)',
          }}
        >
          <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Type</div>
          <div style={{ fontSize: '12px' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>{(log as any).direction === 'outgoing' ? <ArrowUpIcon /> : <ArrowDownIcon />} {log.messageType}</span>
          </div>
        </div>

        <div
          style={{
            padding: '12px',
            background: 'var(--bg-secondary)',
            borderRadius: '6px',
            border: '1px solid var(--border)',
          }}
        >
          <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Status</div>
          <div style={{ fontSize: '12px' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>{log.status === 'completed' ? <CheckCircleIcon /> : log.status === 'failed' ? <XCircleIcon /> : <HourglassIcon />} {log.status}</span>
          </div>
        </div>

        <div
          style={{
            padding: '12px',
            background: 'var(--bg-secondary)',
            borderRadius: '6px',
            border: '1px solid var(--border)',
          }}
        >
          <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Time</div>
          <div style={{ fontSize: '12px' }}>{log.totalTime}ms</div>
        </div>
      </div>

      {/* Message Envelope & Body Viewer */}
      <div style={{ marginBottom: '24px' }}>
        <button
          onClick={toggleRawMessageExpanded}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            marginBottom: '12px',
            fontSize: '14px',
            fontWeight: 400,
            color: 'var(--text-primary)',
          }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center' }}>
            {expandedRawMessage ? <ChevronDownIcon /> : <ChevronRightIcon />}
          </span>
          <MessageEnvelopeIcon size={16} /> Message Envelope & Body
        </button>

        {expandedRawMessage && (
          <div>
            {(log as any).pipelineStart?.envelopeSnapshot ? (
              <div style={{ marginBottom: '16px' }}>
                <JSONVisualizer
                  data={(log as any).pipelineStart.envelopeSnapshot}
                  title={<><MailboxIcon size={14} /> Message Envelope (Input)</>}
                  className="message-visualizer"
                />
              </div>
            ) : null}

            {(log as any).pipelineEnd?.envelopeSnapshot && (log as any).pipelineStart?.envelopeSnapshot !== (log as any).pipelineEnd?.envelopeSnapshot ? (
              <div style={{ marginBottom: '16px' }}>
                <JSONVisualizer
                  data={(log as any).pipelineEnd.envelopeSnapshot}
                  title={<><MailboxIcon size={14} /> Message Envelope (Output)</>}
                  className="message-visualizer"
                />
              </div>
            ) : null}

            {(log as any).rawMessage ? (
              <JSONVisualizer
                data={typeof (log as any).rawMessage === 'string' ? JSON.parse((log as any).rawMessage) : (log as any).rawMessage}
                title={<><PackageIcon size={14} /> Raw Message Body</>}
                className="message-visualizer"
              />
            ) : (log as any).snapshots?.original ? (
              <JSONVisualizer
                data={(log as any).snapshots.original}
                title={<><PackageIcon size={14} /> Original Message</>}
                className="message-visualizer"
              />
            ) : null}
          </div>
        )}
      </div>

      {/* Hook Pipeline */}
      <div style={{ marginBottom: '24px' }}>
        <h3 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}><HooksIcon /> Hook Execution Pipeline</h3>

        {log.hookLogs.length === 0 ? (
          <div style={{ padding: '16px', textAlign: 'center', color: 'var(--text-secondary)' }}>No hooks executed</div>
        ) : (
          log.hookLogs.map((hookLog) => (
            <div
              key={hookLog.hookName}
              style={{
                marginBottom: '12px',
                padding: '0',
                background: hookLog.status === 'error' ? 'rgba(248, 113, 113, 0.1)' : 'var(--bg-secondary)',
                border: '1px solid var(--border)',
                borderRadius: '6px',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '13px',
                  padding: '12px',
                  backgroundColor: hookLog.status === 'error' ? 'rgba(248, 113, 113, 0.05)' : 'transparent',
                  borderBottom: '1px solid var(--border)',
                }}
                onClick={() => toggleHookExpanded(hookLog.hookName)}
              >
                <span style={{ fontSize: '14px' }}>{expandedHooks.has(hookLog.hookName) ? <ChevronDownIcon /> : <ChevronRightIcon />}</span>
                <span style={{ fontSize: '14px', color: hookLog.status === 'success' ? '#27ae60' : hookLog.status === 'error' ? '#e74c3c' : '#f39c12' }}>
                  {hookLog.status === 'success' ? <CheckCircleIcon /> : hookLog.status === 'error' ? <XCircleIcon /> : <SkipForwardIcon />}
                </span>
                <span>{hookLog.hookName}</span>

                {/* Status Badge */}
                <div style={{
                  marginLeft: 'auto',
                  display: 'flex',
                  gap: '8px',
                  alignItems: 'center'
                }}>
                  <div style={{
                    fontSize: '10px',
                    fontWeight: 700,
                    padding: '3px 8px',
                    borderRadius: '3px',
                    background: hookLog.status === 'success' ? 'rgba(39, 174, 96, 0.2)' : hookLog.status === 'error' ? 'rgba(248, 113, 113, 0.2)' : 'rgba(243, 156, 18, 0.2)',
                    color: hookLog.status === 'success' ? '#27ae60' : hookLog.status === 'error' ? '#e74c3c' : '#f39c12',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    {hookLog.status}
                  </div>

                  {/* Priority Badge */}
                  <div style={{
                    fontSize: '10px',
                    fontWeight: 700,
                    padding: '3px 8px',
                    borderRadius: '3px',
                    background: 'rgba(66, 153, 225, 0.2)',
                    color: '#4299e1',
                    textTransform: 'uppercase',
                  }}>
                    P{hookLog.priority}
                  </div>

                  {/* Execution Time Badge */}
                  <div style={{
                    fontSize: '10px',
                    fontWeight: 700,
                    padding: '3px 8px',
                    borderRadius: '3px',
                    background: 'rgba(128, 90, 213, 0.2)',
                    color: '#805ad5',
                  }}>
                    <ClockIcon size={12} /> {hookLog.executionTime}ms
                  </div>
                </div>
              </div>

              {expandedHooks.has(hookLog.hookName) && (
                <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: '12px', borderTop: '1px solid var(--color-border)' }}>
                  {/* Data Changed Badge */}
                  {(hookLog as any).dataChanged !== undefined && (
                    <div>
                      <span style={{
                        fontSize: '11px',
                        padding: '4px 10px',
                        borderRadius: '4px',
                        backgroundColor: (hookLog as any).dataChanged ? 'rgba(252, 176, 64, 0.2)' : 'rgba(39, 174, 96, 0.2)',
                        color: (hookLog as any).dataChanged ? '#f59e0b' : '#10b981',
                        fontWeight: 700,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                      }}>
                        {(hookLog as any).dataChanged ? <><PencilIcon size={11} /> DATA CHANGED</> : <><SearchIcon size={11} /> READ-ONLY</>}
                      </span>
                    </div>
                  )}

                  {/* Metadata Section */}
                  {((hookLog as any).description || (hookLog as any).version || (hookLog as any).author) && (
                    <div style={{ marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid var(--color-border)' }}>
                      {(hookLog as any).description && (
                        <div style={{ fontSize: '12px', marginBottom: '8px', color: 'var(--color-text-primary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <NoteIcon size={14} /> {(hookLog as any).description}
                        </div>
                      )}
                      {((hookLog as any).version || (hookLog as any).author) && (
                        <div style={{ display: 'flex', gap: '16px', fontSize: '11px', color: 'var(--color-text-secondary)' }}>
                          {(hookLog as any).version && <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><PackageIcon size={12} /> v{(hookLog as any).version}</span>}
                          {(hookLog as any).author && <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><UserIcon size={12} /> {(hookLog as any).author}</span>}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Tags and Impact Level */}
                  {((hookLog as any).tags || (hookLog as any).impactLevel) && (
                    <div style={{ marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid var(--color-border)' }}>
                      {(hookLog as any).tags && (
                        <div style={{ marginBottom: '8px' }}>
                          {((hookLog as any).tags || []).map((tag: string) => (
                            <span
                              key={tag}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                fontSize: '10px',
                                padding: '3px 8px',
                                marginRight: '6px',
                                marginBottom: '4px',
                                backgroundColor: 'rgba(66, 153, 225, 0.15)',
                                color: '#4299e1',
                                borderRadius: '3px',
                                border: '1px solid rgba(66, 153, 225, 0.3)',
                                fontWeight: 600,
                              }}
                            >
                              <TagIcon size={10} /> {tag}
                            </span>
                          ))}
                        </div>
                      )}
                      {(hookLog as any).impactLevel && (
                        <div>
                          <span style={{
                            fontSize: '11px',
                            padding: '4px 10px',
                            borderRadius: '4px',
                            fontWeight: 700,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            backgroundColor:
                              (hookLog as any).impactLevel === 'critical' ? 'rgba(248, 81, 73, 0.2)' :
                              (hookLog as any).impactLevel === 'high' ? 'rgba(252, 176, 64, 0.2)' :
                              (hookLog as any).impactLevel === 'medium' ? 'rgba(240, 136, 62, 0.2)' :
                              'rgba(39, 174, 96, 0.2)',
                            color:
                              (hookLog as any).impactLevel === 'critical' ? '#ef4444' :
                              (hookLog as any).impactLevel === 'high' ? '#f59e0b' :
                              (hookLog as any).impactLevel === 'medium' ? '#f97316' :
                              '#10b981',
                          }}>
                            <ZapIcon size={11} /> Impact: {((hookLog as any).impactLevel || 'unknown').toUpperCase()}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Data Modification Flag */}
                  {(hookLog as any).modifiesData !== undefined && (
                    <div style={{ marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid var(--color-border)' }}>
                      <span style={{
                        fontSize: '11px',
                        padding: '4px 10px',
                        borderRadius: '4px',
                        fontWeight: 700,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        backgroundColor: (hookLog as any).modifiesData ? 'rgba(248, 113, 113, 0.15)' : 'rgba(39, 174, 96, 0.15)',
                        color: (hookLog as any).modifiesData ? '#ef4444' : '#10b981',
                      }}>
                        {(hookLog as any).modifiesData ? <><PencilIcon size={11} /> Modifies Data</> : <><SearchIcon size={11} /> Read-Only</>}
                      </span>
                    </div>
                  )}

                  {/* Input Envelope */}
                  {(hookLog as any).inputEnvelopeSnapshot && (
                    <JSONVisualizer
                      data={(hookLog as any).inputEnvelopeSnapshot}
                      title={<><InboxIcon size={14} /> Input Envelope</>}
                      className="hook-envelope-visualizer"
                    />
                  )}

                  {/* Output Envelope */}
                  {(hookLog as any).outputEnvelopeSnapshot && (
                    <JSONVisualizer
                      data={(hookLog as any).outputEnvelopeSnapshot}
                      title={<><OutboxIcon size={14} /> Output Envelope</>}
                      className="hook-envelope-visualizer"
                    />
                  )}

                  {/* Data Diff */}
                  {(hookLog as any).dataChanged && (hookLog as any).inputEnvelopeSnapshot && (hookLog as any).outputEnvelopeSnapshot && (
                    <div>
                      <h4 style={{ margin: '0 0 8px 0', fontSize: '11px', fontWeight: 700, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '4px' }}><RefreshIcon size={11} /> Body Changes</h4>
                      <DiffDisplay
                        before={(hookLog as any).inputEnvelopeSnapshot.body}
                        after={(hookLog as any).outputEnvelopeSnapshot.body}
                      />
                    </div>
                  )}

                  {/* Hook Activity Log */}
                  {(hookLog as any).activityLog && (hookLog as any).activityLog.length > 0 && (
                    <div>
                      <h4 style={{ margin: '0 0 8px 0', fontSize: '11px', fontWeight: 700, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '4px' }}><ClipboardIcon size={11} /> Hook Activities</h4>
                      <ActivityLogViewer activities={(hookLog as any).activityLog} />
                    </div>
                  )}

                  {/* Message */}
                  {hookLog.message && (
                    <div style={{ fontSize: '12px', color: 'var(--color-text-primary)' }}>
                      {hookLog.message}
                    </div>
                  )}

                  {/* Error Display */}
                  {hookLog.error && (
                    <div
                      style={{
                        padding: '12px',
                        background: 'rgba(248, 113, 113, 0.15)',
                        borderLeft: '3px solid #ef4444',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontFamily: 'monospace',
                        color: '#991b1b',
                      }}
                    >
                      <div style={{ fontWeight: 700, marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}><CloseIcon size={14} /> Error</div>
                      {hookLog.error.message}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Subscriber Delivery */}
      <div>
        <h3 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}><MailboxIcon /> Subscriber Delivery</h3>

        {log.subscriberLogs.length === 0 ? (
          <div style={{ padding: '16px', textAlign: 'center', color: 'var(--text-secondary)' }}>No subscribers</div>
        ) : (
          log.subscriberLogs.map((subLog) => (
            <div
              key={subLog.subscriberId}
              style={{
                marginBottom: '12px',
                padding: '0',
                background: subLog.status === 'error' ? 'rgba(248, 113, 113, 0.1)' : 'var(--bg-secondary)',
                border: '1px solid var(--border)',
                borderRadius: '6px',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '13px',
                  padding: '12px',
                  backgroundColor: subLog.status === 'error' ? 'rgba(248, 113, 113, 0.05)' : 'transparent',
                  borderBottom: '1px solid var(--border)',
                }}
                onClick={() => toggleSubscriberExpanded(subLog.subscriberId)}
              >
                <span style={{ fontSize: '14px' }}>{expandedSubscribers.has(subLog.subscriberId) ? <ChevronDownIcon /> : <ChevronRightIcon />}</span>

                {/* Status Badge */}
                <span style={{
                  fontSize: '11px',
                  fontWeight: 700,
                  padding: '3px 8px',
                  borderRadius: '3px',
                  backgroundColor:
                    subLog.status === 'delivered' ? 'rgba(39, 174, 96, 0.2)' :
                    subLog.status === 'error' ? 'rgba(248, 81, 73, 0.2)' :
                    'rgba(240, 136, 62, 0.2)',
                  color:
                    subLog.status === 'delivered' ? '#10b981' :
                    subLog.status === 'error' ? '#dc2626' :
                    '#f59e0b',
                  minWidth: '70px',
                  textAlign: 'center',
                }}>
                  {subLog.status === 'delivered' && 'Delivered'}
                  {subLog.status === 'error' && 'Error'}
                  {subLog.status === 'timeout' && 'Timeout'}
                </span>

                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '13px', fontWeight: 700 }}>{subLog.subscriberId}</div>
                  <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                    Topic: {subLog.topic} • Time: {subLog.processingTime ? `${subLog.processingTime}ms` : 'N/A'}
                  </div>
                </div>
              </div>

              {expandedSubscribers.has(subLog.subscriberId) && (
                <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: '12px', borderTop: '1px solid var(--border)' }}>
                  {/* Metadata Section */}
                  {((subLog as any).description || (subLog as any).version || (subLog as any).author) && (
                    <div style={{ marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid var(--border)' }}>
                      {(subLog as any).description && (
                        <div style={{ fontSize: '12px', marginBottom: '8px', color: 'var(--color-text-primary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <NoteIcon size={14} /> {(subLog as any).description}
                        </div>
                      )}
                      {((subLog as any).version || (subLog as any).author) && (
                        <div style={{ display: 'flex', gap: '16px', fontSize: '11px', color: 'var(--color-text-secondary)' }}>
                          {(subLog as any).version && <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><PackageIcon size={12} /> v{(subLog as any).version}</span>}
                          {(subLog as any).author && <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><UserIcon size={12} /> {(subLog as any).author}</span>}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Tags and Impact Level */}
                  {((subLog as any).tags || (subLog as any).impactLevel) && (
                    <div style={{ marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid var(--border)' }}>
                      {(subLog as any).tags && (
                        <div style={{ marginBottom: '8px' }}>
                          {((subLog as any).tags || []).map((tag: string) => (
                            <span
                              key={tag}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                fontSize: '10px',
                                padding: '3px 8px',
                                marginRight: '6px',
                                marginBottom: '4px',
                                backgroundColor: 'rgba(66, 153, 225, 0.15)',
                                color: '#4299e1',
                                borderRadius: '3px',
                                border: '1px solid rgba(66, 153, 225, 0.3)',
                                fontWeight: 600,
                              }}
                            >
                              <TagIcon size={10} /> {tag}
                            </span>
                          ))}
                        </div>
                      )}
                      {(subLog as any).impactLevel && (
                        <div>
                          <span style={{
                            fontSize: '11px',
                            padding: '4px 10px',
                            borderRadius: '4px',
                            fontWeight: 700,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            backgroundColor:
                              (subLog as any).impactLevel === 'critical' ? 'rgba(248, 81, 73, 0.2)' :
                              (subLog as any).impactLevel === 'high' ? 'rgba(252, 176, 64, 0.2)' :
                              (subLog as any).impactLevel === 'medium' ? 'rgba(240, 136, 62, 0.2)' :
                              'rgba(39, 174, 96, 0.2)',
                            color:
                              (subLog as any).impactLevel === 'critical' ? '#dc2626' :
                              (subLog as any).impactLevel === 'high' ? '#f59e0b' :
                              (subLog as any).impactLevel === 'medium' ? '#f97316' :
                              '#10b981',
                          }}>
                            <ZapIcon size={11} /> Impact: {((subLog as any).impactLevel || 'unknown').toUpperCase()}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Blocking Flag */}
                  {(subLog as any).isBlocking !== undefined && (
                    <div style={{ marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid var(--border)' }}>
                      <span style={{
                        fontSize: '11px',
                        padding: '4px 10px',
                        borderRadius: '4px',
                        fontWeight: 700,
                        backgroundColor: (subLog as any).isBlocking ? 'rgba(248, 113, 113, 0.15)' : 'rgba(39, 174, 96, 0.15)',
                        color: (subLog as any).isBlocking ? '#ef4444' : '#10b981',
                      }}>
                        {(subLog as any).isBlocking ? <><CloseIcon size={11} /> Blocking</> : <><CheckIcon size={11} /> Non-Blocking</>}
                      </span>
                    </div>
                  )}

                  {/* Data Modification Flag */}
                  {(subLog as any).modifiesData !== undefined && (
                    <div style={{ marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid var(--border)' }}>
                      <span style={{
                        fontSize: '11px',
                        padding: '4px 10px',
                        borderRadius: '4px',
                        fontWeight: 700,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        backgroundColor: (subLog as any).modifiesData ? 'rgba(248, 113, 113, 0.15)' : 'rgba(39, 174, 96, 0.15)',
                        color: (subLog as any).modifiesData ? '#ef4444' : '#10b981',
                      }}>
                        {(subLog as any).modifiesData ? <><PencilIcon size={11} /> Modifies Data</> : <><SearchIcon size={11} /> Read-Only</>}
                      </span>
                    </div>
                  )}

                  {/* Delivered Envelope */}
                  {(subLog as any).inputEnvelopeSnapshot && (
                    <JSONVisualizer
                      data={(subLog as any).inputEnvelopeSnapshot}
                      title={<><MailboxIcon size={14} /> Delivered Envelope</>}
                      className="subscriber-envelope-visualizer"
                    />
                  )}

                  {/* Subscriber Activity Log */}
                  {(subLog as any).activityLog && (subLog as any).activityLog.length > 0 && (
                    <div>
                      <h4 style={{ margin: '0 0 8px 0', fontSize: '11px', fontWeight: 700, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '4px' }}><ClipboardIcon size={11} /> Subscriber Activities</h4>
                      <ActivityLogViewer activities={(subLog as any).activityLog} />
                    </div>
                  )}

                  {/* Message */}
                  {subLog.message && (
                    <div style={{ fontSize: '12px', color: 'var(--color-text-primary)' }}>
                      {subLog.message}
                    </div>
                  )}

                  {/* Error Display */}
                  {subLog.error && (
                    <div
                      style={{
                        padding: '12px',
                        background: 'rgba(248, 113, 113, 0.15)',
                        borderLeft: '3px solid #ef4444',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontFamily: 'monospace',
                        color: '#991b1b',
                      }}
                    >
                      <div style={{ fontWeight: 700, marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}><CloseIcon size={14} /> Error</div>
                      {subLog.error.message}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  )
}

function DiffDisplay({ before, after }: { before: unknown; after: unknown }) {
  const diffs = DiffEngine.compute(before, after)

  return (
    <div
      style={{
        padding: '12px',
        background: 'var(--bg-secondary)',
        borderRadius: '6px',
        border: '1px solid var(--border)',
        fontSize: '11px',
        fontFamily: 'monospace',
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}><NoteIcon /> Body Changes</div>
      {diffs.map((diff, idx) => (
        <div
          key={idx}
          style={{
            padding: '4px 8px',
            margin: '2px 0',
            background:
              diff.type === 'added' ? 'rgba(39, 174, 96, 0.15)' : diff.type === 'removed' ? 'rgba(231, 76, 60, 0.15)' : 'rgba(243, 156, 18, 0.15)',
            color:
              diff.type === 'added' ? '#27ae60' : diff.type === 'removed' ? '#e74c3c' : '#f39c12',
            borderRadius: '3px',
            wordBreak: 'break-all',
          }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>{diff.type === 'added' ? <><CheckCircleIcon /> +</> : diff.type === 'removed' ? <><XCircleIcon /> -</> : '~'}</span>{' '}{diff.path}:{' '}
          {diff.type !== 'removed' && String(diff.after).substring(0, 50)}
        </div>
      ))}
    </div>
  )
}
